/**
 * Admin MFA self-service hooks (Batch 31a.2.3).
 *
 * Distinct from `hooks/queries/auth/useMfaEnroll[Confirm]` which target
 * the bootstrap/login path with mfa_pending JWTs. These hooks target
 * the in-profile self-enable + disable flow with a regular SA access
 * JWT — the apiClient interceptor adds Authorization automatically.
 *
 * - `useAdminMfaCredentials`  GET  /admin/mfa/credentials
 * - `useEnableAdminMfa`       POST /admin/mfa/enroll
 * - `useEnableAdminMfaConfirm` POST /admin/mfa/enroll/confirm
 * - `useDisableAdminMfa`      DELETE /admin/mfa/credentials/{id}
 *
 * Reuses the response types from `auth/useMfaEnroll[Confirm]` so the
 * shared `MfaEnrollFlow` component can consume either set of mutations.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import type { MfaEnrollResponse } from "@/hooks/queries/auth/useMfaEnroll";
import type { MfaEnrollConfirmResponse } from "@/hooks/queries/auth/useMfaEnrollConfirm";

export interface AdminMfaCredentialItem {
  credential_id: string;
  kind: "totp";
  status: "pending_confirm" | "active";
  enrolled_at: string;
  confirmed_at: string | null;
  last_used_at: string | null;
  recovery_codes_remaining: number;
}

interface AdminMfaCredentialsListResponse {
  items: AdminMfaCredentialItem[];
}

const ADMIN_MFA_CREDENTIALS_KEY = ["admin", "mfa", "credentials"] as const;

/** GET /admin/mfa/credentials — read the SA's MFA state for the Profile section. */
export function useAdminMfaCredentials() {
  return useQuery<AdminMfaCredentialsListResponse, ApiError>({
    queryKey: ADMIN_MFA_CREDENTIALS_KEY,
    queryFn: async () => {
      const resp = await apiClient.get<AdminMfaCredentialsListResponse>(
        "/admin/mfa/credentials",
      );
      return resp.data;
    },
    // Status changes only when the user explicitly enables/disables.
    staleTime: 30_000,
  });
}

/** POST /admin/mfa/enroll — self-enable path via access JWT (HARD-4). */
export function useEnableAdminMfa() {
  return useMutation<MfaEnrollResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<MfaEnrollResponse>(
        "/admin/mfa/enroll",
      );
      return resp.data;
    },
  });
}

/** POST /admin/mfa/enroll/confirm — self-enable confirm via access JWT. */
export function useEnableAdminMfaConfirm() {
  return useMutation<MfaEnrollConfirmResponse, ApiError, { code: string }>({
    mutationFn: async ({ code }) => {
      const resp = await apiClient.post<MfaEnrollConfirmResponse>(
        "/admin/mfa/enroll/confirm",
        { code },
      );
      return resp.data;
    },
  });
}

/** DELETE /admin/mfa/credentials/{id} — disable own MFA. Body carries
 * password + current TOTP per the existing AdminMfaCredentialDeleteRequest
 * contract. Backend HARD-6 revokes ALL active sessions on success. */
export function useDisableAdminMfa() {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiError,
    { credential_id: string; current_password: string; current_totp: string }
  >({
    mutationFn: async ({ credential_id, current_password, current_totp }) => {
      await apiClient.delete(`/admin/mfa/credentials/${credential_id}`, {
        data: { current_password, current_totp },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_MFA_CREDENTIALS_KEY });
    },
  });
}
